import { FieldItem, ValidateRule } from '@/coreDomainsModule/constant/validateRules';
import { FormValidateFunctionFactory } from '@/coreDomainsModule/factories/formValidateFunctionFactory';
import { FormErrorMessageFactory } from '@/coreDomainsModule/factories/formErrorMessageFactory';
import { computed, ref, Ref } from 'vue';

/**
 * useForm State Type
 */
export type FieldState = Record<
  string,
  {
    isError: Ref<boolean>;
    errorMessage: Ref<string>;
    validate: ReturnType<typeof FormValidateFunctionFactory.create>;
    value: Ref<string>;
    setValue: (value: string, shouldValidate?: boolean) => void;
    rules: readonly ValidateRule[];
  }
>;

/**
 *　　フォームの値を管理する
 * @param fieldItems - フィールドのアイテム
 */
export const useForms = (fieldItems: FieldItem[]) => {
  const state: FieldState = {};

  for (const fieldItem of fieldItems) {
    state[fieldItem.name] = {
      value: ref(''),
      isError: ref(false),
      errorMessage: ref(''),
      validate: FormValidateFunctionFactory.create(fieldItem.rules),
      /**
       * フィールドの値を設定
       * @param value - 設定する値
       * @param shouldValidate - バリデーションを実行するかどうか
       */
      setValue: (value, shouldValidate = true) => {
        state[fieldItem.name].value.value = value;
        if (!shouldValidate) return;
        const { isValid, invalidRules } = state[fieldItem.name].validate(value);
        state[fieldItem.name].isError.value = !isValid;
        state[fieldItem.name].errorMessage.value = FormErrorMessageFactory.create(invalidRules);
      },
      rules: fieldItem.rules,
    };
  }

  /**
   * 全てのバリデーションを実行
   */
  const allValidate = (): boolean => {
    let result = true;
    for (const key in state) {
      const { isValid, invalidRules } = state[key].validate(state[key].value.value);
      if (!isValid) {
        state[key].isError.value = true;
        state[key].errorMessage.value = FormErrorMessageFactory.create(invalidRules);
        result = false;
      }
    }
    return result;
  };

  /**
   * フォームが全てOKかどうか
   */
  const isAllValid = computed(() => {
    return Object.values(state).every((field) => !field.isError.value);
  });

  /**
   * フォームのリセット（初期化）
   * @param fieldItems - フィールドのアイテム
   */
  const resetForm = (fieldItems: FieldItem[] = []): void => {
    // 対応するフィールドの初期化
    // 対応するフィールドがない場合は新規に追加
    for (const fieldItem of fieldItems) {
      if (state[fieldItem.name]) {
        state[fieldItem.name].value.value = '';
        state[fieldItem.name].isError.value = false;
        state[fieldItem.name].errorMessage.value = '';
        state[fieldItem.name].validate = FormValidateFunctionFactory.create(fieldItem.rules);
        state[fieldItem.name].rules = fieldItem.rules;
      } else {
        state[fieldItem.name] = {
          value: ref(''),
          isError: ref(false),
          errorMessage: ref(''),
          validate: FormValidateFunctionFactory.create(fieldItem.rules),
          /**
           * フィールドの値を設定
           * @param value - 設定する値
           */
          setValue: (value) => {
            state[fieldItem.name].value.value = value;
            const { isValid, invalidRules } = state[fieldItem.name].validate(value);
            state[fieldItem.name].isError.value = !isValid;
            state[fieldItem.name].errorMessage.value = FormErrorMessageFactory.create(invalidRules);
          },
          rules: fieldItem.rules,
        };
      }
    }
    // 対応するフィールドがない場合は削除
    for (const key in state) {
      if (!fieldItems.some((fieldItem) => fieldItem.name === key)) {
        delete state[key];
      }
    }
  };

  return {
    fieldState: state,
    allValidate,
    resetForm,
    isAllValid,
  };
};
